/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row, Spinner } from "react-bootstrap";
import { api } from "core/api";
import { Card, SvgIcon, Table } from "core/components";
import { ProjectFormData } from "core/schema";
import { payrolls_admin_project_path } from "routes";
import formatter from "core/formatter";
import moment from "moment";

type PayrollEntry = {
  id: number | string;
  cut_off_label: string | null;
  payroll_date: string;
  cutoff_start_date: string;
  cutoff_end_date: string;
  cost_item_code: string | null;
  cost_item_name: string | null;
  total_hours: string | number | null;
  total_amount: string | number | null;
};

const formatHours = (value: string | number | null | undefined) => {
  const num = Number(value || 0);
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

type ProjectPayrollsProps = {
  project: ProjectFormData;
};

const formatDate = (value?: string) => (value ? moment(value).format("MM/DD/YYYY") : "-");

const ProjectPayrolls = ({ project }: ProjectPayrollsProps): JSX.Element => {
  const [payrolls, setPayrolls] = React.useState<PayrollEntry[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const getPayrolls = React.useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await api.get<PayrollEntry[]>(payrolls_admin_project_path({ id: project.id }));
      setPayrolls(response.data || []);
    } catch (error) {
      console.error(error);
      setErrorMessage("Unable to fetch payrolls from the payroll system. Please try again later.");
      setPayrolls([]);
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  React.useEffect(() => {
    getPayrolls().catch(console.error);
  }, [getPayrolls]);

  const columns = React.useMemo(
    () => [
      {
        Header: "Payroll Run #",
        accessor: "id",
        Cell: (props) => <p className="text-center">{props.value}</p>
      },
      {
        Header: "Cutoff ID",
        accessor: "cut_off_label",
        Cell: (props) => <p className="text-center">{props.value}</p>
      },
      {
        Header: "Payroll Date",
        accessor: "payroll_date",
        Cell: (props) => <p className="text-center">{formatDate(props.value)}</p>
      },
      {
        Header: "Cutoff Start Date",
        accessor: "cutoff_start_date",
        Cell: (props) => <p className="text-center">{formatDate(props.value)}</p>
      },
      {
        Header: "Cutoff End Date",
        accessor: "cutoff_end_date",
        Cell: (props) => <p className="text-center">{formatDate(props.value)}</p>
      },
      {
        Header: "Cost Item",
        accessor: "cost_item_code",
        Cell: (props) => {
          const code = props.value;
          const name = props.row.original.cost_item_name;
          const label = code && name ? `${code} - ${name}` : code || "-";
          return (
            <p className="text-start mb-0 text-truncate" title={label} data-bs-toggle="tooltip">
              {label}
            </p>
          );
        }
      },
      {
        Header: () => <div className="text-right">Total Hours</div>,
        accessor: "total_hours",
        Cell: (props) => {
          const hours = formatHours(props.value);
          return (
            <div title={hours} data-bs-toggle="tooltip">
              <p className="text-right text-truncate pe-3 mb-0">{hours}</p>
            </div>
          );
        }
      },
      {
        Header: () => <div className="text-right">Total Amount</div>,
        accessor: "total_amount",
        Cell: (props) => {
          const total = formatter.format(Number(props.value || 0));
          return (
            <div title={total} data-bs-toggle="tooltip">
              <p className="text-right text-truncate pe-3 mb-0">{total}</p>
            </div>
          );
        }
      }
    ],
    []
  );

  const loadingState = () => (
    <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
      <Row>
        <Col>
          <Spinner animation="border" role="status" />
        </Col>
      </Row>
    </Container>
  );

  const errorState = () => (
    <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
      <Row className="mb-4">
        <Col>
          <SvgIcon name="exclamation" className="display-icon text-gray" />
        </Col>
      </Row>
      <Row>
        <Col>
          <h5>{errorMessage}</h5>
        </Col>
      </Row>
    </Container>
  );

  const emptyState = () => (
    <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
      <Row className="mb-4">
        <Col>
          <SvgIcon name="clipboard-list" className="display-icon text-gray" />
        </Col>
      </Row>
      <Row>
        <Col>
          <h5>No payroll records for this project yet.</h5>
        </Col>
      </Row>
    </Container>
  );

  const body = () => {
    if (loading) return loadingState();
    if (errorMessage) return errorState();
    if (payrolls.length === 0) return emptyState();
    return (
      <Row>
        <Col>
          <Table columns={columns} data={payrolls} />
        </Col>
      </Row>
    );
  };

  return (
    <Card.Body>
      <Container fluid={true}>{body()}</Container>
    </Card.Body>
  );
};

export default ProjectPayrolls;
